import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { usePairingInventoryParams } from '@hungryroot/inventory';
import {
  getApiV3CustomerPairings,
  getApiV3Pairings,
  StatusCode,
  type CustomerPairingsParamsV3,
  type PairingListResultV3,
} from '@hungryroot/rest';

import {
  DIET_BUCKETS,
  KID_FRIENDLY_TAG_ID,
  PROTEIN_BUCKETS,
  type DietBucket,
  type ProteinBucket,
} from '../utils/sectionTags';
import { selectTopRated } from '../utils/selectTopRated';

const CANDIDATE_LIMIT = 50;
const KID_RECIPES_DISPLAY_COUNT = 6;
const TIME_LIMIT_15_MINUTES = 15 * 60 * 1000;
const HYDRATION_BATCH_SIZE = 100;

export type PairingForDisplay = Omit<PairingListResultV3, 'tags'>;

export interface RecipeBucketResult {
  key: string;
  label: string;
  recipe: PairingForDisplay;
}

export interface UseHouseholdVarietyRecipesResult {
  proteinSection: RecipeBucketResult[];
  dietSection: RecipeBucketResult[];
  kidRecipes: PairingForDisplay[];
  isLoading: boolean;
  isError: boolean;
}

type DietaryRestrictionFlag = keyof Pick<
  CustomerPairingsParamsV3,
  | 'is_gluten_free'
  | 'is_dairy_free'
  | 'is_vegan'
  | 'is_vegetarian'
  | 'is_pescatarian'
  | 'is_nut_free'
  | 'is_peanut_free'
  | 'is_egg_free'
  | 'is_soy_free'
  | 'is_shellfish_free'
  | 'is_sesame_free'
  | 'is_garlic_free'
  | 'is_onion_free'
  | 'is_grain_free'
  | 'is_caffeine_free'
>;

const DIETARY_RESTRICTION_FLAGS: ReadonlySet<DietaryRestrictionFlag> = new Set([
  'is_gluten_free',
  'is_dairy_free',
  'is_vegan',
  'is_vegetarian',
  'is_pescatarian',
  'is_nut_free',
  'is_peanut_free',
  'is_egg_free',
  'is_soy_free',
  'is_shellfish_free',
  'is_sesame_free',
  'is_garlic_free',
  'is_onion_free',
  'is_grain_free',
  'is_caffeine_free',
]);

function isDietaryRestrictionFlag(
  fieldName: string
): fieldName is DietaryRestrictionFlag {
  return DIETARY_RESTRICTION_FLAGS.has(fieldName as DietaryRestrictionFlag);
}

type BucketKey =
  | { kind: 'protein'; bucket: ProteinBucket }
  | { kind: 'diet'; bucket: DietBucket }
  | { kind: 'kid' };

interface FetchedBucket {
  spec: BucketKey;
  candidateIds: number[];
}

function buildCandidateParams(
  spec: BucketKey,
  baseParams: CustomerPairingsParamsV3
): CustomerPairingsParamsV3 {
  if (spec.kind === 'protein') {
    return {
      ...baseParams,
      protein_tags: spec.bucket.proteinTagIds,
      ...spec.bucket.additionalParams,
    };
  }
  if (spec.kind === 'diet') {
    return { ...baseParams, nutrition_tags: [spec.bucket.nutritionTagId] };
  }
  return { ...baseParams, tag: KID_FRIENDLY_TAG_ID };
}

async function fetchCandidates(
  spec: BucketKey,
  baseParams: CustomerPairingsParamsV3
): Promise<FetchedBucket> {
  const params = buildCandidateParams(spec, baseParams);
  const response = await getApiV3CustomerPairings(params);
  if (response.status !== StatusCode.SuccessOK) {
    throw new Error(JSON.stringify(response.data, null, 2));
  }
  return {
    spec,
    candidateIds: response.data.results.map((r) => r.id),
  };
}

/**
 * Fetches the top recipes for each protein, diet, and the kid-approved tag
 * in a single coordinated query so we can do the candidate fan-out + the
 * `/v3/pairings/` hydration in one shot rather than firing 15+ React-Query
 * subscriptions.
 *
 * - Protein cards: one per `PROTEIN_BUCKETS` entry, top-rated within the
 *   bucket. Buckets with zero matches are dropped from the response.
 * - Diet cards: one per `DIET_BUCKETS` entry, same selection rules.
 * - Kid cards: up to `KID_RECIPES_DISPLAY_COUNT` top-rated recipes tagged
 *   `DinnerKids`.
 *
 * "Top-rated" means: drop the bottom half by `num_ratings`, sort by
 * `average_rating` DESC, tiebreak by `points` DESC. See `selectTopRated`.
 */
export function useHouseholdVarietyRecipes(): UseHouseholdVarietyRecipesResult {
  const inventoryParams = usePairingInventoryParams();

  const { data: dietaryRestrictions, isLoading: isDietaryRestrictionsLoading } =
    useCustomerDietaryRestrictions();

  const dietaryFlags = useMemo<
    Partial<Record<DietaryRestrictionFlag, true>>
  >(() => {
    const flags: Partial<Record<DietaryRestrictionFlag, true>> = {};
    for (const restriction of dietaryRestrictions?.dietary_restrictions ?? []) {
      if (isDietaryRestrictionFlag(restriction.field_name)) {
        flags[restriction.field_name] = true;
      }
    }
    return flags;
  }, [dietaryRestrictions?.dietary_restrictions]);

  const baseParams: CustomerPairingsParamsV3 = useMemo(
    () => ({
      has_inventory: true,
      hide_dislike: true,
      exclude_nevers: true,
      limit: CANDIDATE_LIMIT,
      ...dietaryFlags,
      ...(inventoryParams ?? {}),
    }),
    [dietaryFlags, inventoryParams]
  );

  const enabled =
    !isDietaryRestrictionsLoading && inventoryParams !== undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['HouseholdVarietyRecipes', baseParams],
    queryFn: async () => {
      const specs: BucketKey[] = [
        ...PROTEIN_BUCKETS.map(
          (bucket): BucketKey => ({ kind: 'protein', bucket })
        ),
        ...DIET_BUCKETS.map((bucket): BucketKey => ({ kind: 'diet', bucket })),
        { kind: 'kid' },
      ];

      const fetched = await Promise.all(
        specs.map((spec) => fetchCandidates(spec, baseParams))
      );

      const allIds = Array.from(
        new Set(fetched.flatMap((bucket) => bucket.candidateIds))
      );

      if (allIds.length === 0) {
        return {
          proteinSection: [] as RecipeBucketResult[],
          dietSection: [] as RecipeBucketResult[],
          kidRecipes: [] as PairingForDisplay[],
        };
      }

      const byId = new Map<number, PairingForDisplay>();
      for (let i = 0; i < allIds.length; i += HYDRATION_BATCH_SIZE) {
        const batch = allIds.slice(i, i + HYDRATION_BATCH_SIZE);
        const hydration = await getApiV3Pairings({
          ids: batch,
          limit: batch.length,
        });
        if (hydration.status !== StatusCode.SuccessOK) {
          throw new Error(JSON.stringify(hydration.data, null, 2));
        }
        for (const pairing of hydration.data.results) {
          byId.set(pairing.id, pairing);
        }
      }

      function pickForBucket(candidateIds: number[]): PairingForDisplay[] {
        const hydrated = candidateIds
          .map((id) => byId.get(id))
          .filter((p): p is PairingForDisplay => p !== undefined);
        return selectTopRated(hydrated);
      }

      const proteinSection: RecipeBucketResult[] = [];
      const dietSection: RecipeBucketResult[] = [];
      let kidRecipes: PairingForDisplay[] = [];

      const usedRecipeIds = new Set<number>();

      for (const { spec, candidateIds } of fetched) {
        const ranked = pickForBucket(candidateIds);
        if (spec.kind === 'protein') {
          const top = ranked[0];
          if (top) {
            usedRecipeIds.add(top.id);
            proteinSection.push({
              key: spec.bucket.key,
              label: spec.bucket.label,
              recipe: top,
            });
          }
        } else if (spec.kind === 'diet') {
          const top = ranked.find((r) => !usedRecipeIds.has(r.id));
          if (top) {
            usedRecipeIds.add(top.id);
            dietSection.push({
              key: spec.bucket.key,
              label: spec.bucket.label,
              recipe: top,
            });
          }
        } else {
          kidRecipes = ranked.slice(0, KID_RECIPES_DISPLAY_COUNT);
        }
      }

      return { proteinSection, dietSection, kidRecipes };
    },
    enabled,
    gcTime: TIME_LIMIT_15_MINUTES,
    staleTime: TIME_LIMIT_15_MINUTES,
    retry: 0,
  });

  return {
    proteinSection: data?.proteinSection ?? [],
    dietSection: data?.dietSection ?? [],
    kidRecipes: data?.kidRecipes ?? [],
    isLoading: !enabled || isLoading,
    isError,
  };
}
